'use client';

import type { Lead, LeadStatus } from '@up2stay/up2promo-api-client';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SegmentedControl,
} from '@up2stay/up2promo-ui';
import {
  Calendar,
  Lock,
  ScanLine,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { LeadAssigneePicker } from '@/core/components/leads/lead-assignee-picker';
import {
  type ContactForm,
  type ContactFormField,
  type LeadCrmProps,
  LeadDetailProperties,
} from '@/core/components/leads/lead-detail/lead-detail-properties';
import {
  LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS,
  LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS,
  LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS,
} from '@/core/components/leads/lead-detail/lead-detail-field-styles';

type OriginLabels = {
  title: string;
  source: string;
  capturedBy: string;
  event: string;
  consent: string;
  consentYes: string;
  consentNo: string;
};

type AssignmentLabels = {
  title: string;
  visibility: string;
  private: string;
  workspace: string;
  hintWorkspace: string;
  hintPrivate: string;
};

type LeadDetailSidebarProps = {
  lead: Lead;
  locale: string;
  /** Desktop sidebar vs mobile stacked panels. */
  variant?: 'desktop' | 'mobile';
  labels: {
    contact: string;
    properties: string;
    status: string;
    email: string;
    phone: string;
    company: string;
    jobTitle: string;
    address: string;
    /** "{field} kopieren" — name of the copy buttons (DEV-540). */
    copyField: string;
  };
  assignmentLabels: AssignmentLabels;
  originLabels: OriginLabels;
  statusLabels: Record<string, string>;
  sourceLabel: string;
  primaryEventName: string | null;
  consentDisplay: string;
  contactForm: ContactForm;
  isSavingContact: boolean;
  onContactFormChange: (form: ContactForm) => void;
  onContactFieldBlur: (field: ContactFormField) => void;
  contactEmailError?: string | null;
  onStatusChange: (status: LeadStatus) => void;
  onCopy: () => void;
  crm: LeadCrmProps;
  eventsSlot: ReactNode;
  tagsSlot: ReactNode;
};

function OriginRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof User;
  label: string;
  value: string;
}) {
  return (
    <div className="grid min-h-[38px] min-w-0 grid-cols-[22px_minmax(0,5.75rem)_minmax(0,1fr)] items-center gap-2 border-t border-[color:var(--surface-border)] py-1 first:border-t-0">
      <Icon
        className="size-[15px] shrink-0 text-muted-foreground"
        aria-hidden
      />
      <span className="truncate text-[12.5px] text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 truncate text-[13px] text-foreground">
        {value || '—'}
      </span>
    </div>
  );
}

function AssignmentPanel({
  crm,
  assignmentLabels,
  eventsSlot,
  tagsSlot,
  showAssignee = true,
}: {
  crm: LeadCrmProps;
  assignmentLabels: AssignmentLabels;
  eventsSlot: ReactNode;
  tagsSlot: ReactNode;
  /** Desktop comps keep assignee in the hero; mobile keeps it in Zuordnung. */
  showAssignee?: boolean;
}) {
  const workspaceId = crm.workspaceId?.trim() ?? '';
  const visibilityValue = crm.visibilityIsWorkspace ? 'workspace' : 'private';
  const canToggleVisibility =
    Boolean(workspaceId) && !crm.leadsSharingLockedByWorkspace && crm.editable;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
        <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
          {assignmentLabels.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-[18px] pb-[18px] pt-0">
        {showAssignee ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] text-muted-foreground">
              {crm.assigneeLabel}
            </span>
            {workspaceId ? (
              <LeadAssigneePicker
                workspaceId={workspaceId}
                ownerUserId={crm.leadOwnerUserId}
                ownerOptionLabel={crm.recordOwnerAssigneeLabel}
                assigneeUserId={crm.assigneeUserId ?? undefined}
                triggerLabel={crm.assigneeTriggerLabel}
                triggerClassName="flex w-full max-w-full cursor-pointer items-center py-0 text-sm font-normal leading-5 text-foreground"
                disabled={!workspaceId}
                editable={crm.editable}
                placeholder={crm.assigneePlaceholder}
                labels={crm.assigneePickerLabels}
                locale={crm.locale}
                isPrivate={crm.isPrivate}
                workspaceLeadsAlwaysShared={crm.leadsSharingLockedByWorkspace}
                showSavedCue
                onAssigneeChange={(next, label) =>
                  crm.onAssigneeChange?.(next, label)
                }
              />
            ) : (
              <span className="text-[13px] text-foreground">
                {crm.assigneeDisplayReadonly ||
                  crm.assigneePickerLabels?.nobody ||
                  '—'}
              </span>
            )}
          </div>
        ) : null}

        {workspaceId && !crm.leadsSharingLockedByWorkspace ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] text-muted-foreground">
              {assignmentLabels.visibility}
            </span>
            <SegmentedControl
              fullWidth
              ariaLabel={assignmentLabels.visibility}
              value={visibilityValue}
              onChange={(next) => {
                if (!canToggleVisibility) return;
                crm.onShareChange?.(next === 'workspace');
              }}
              itemClassName="h-auto gap-1.5 px-2.5 py-1.5 text-[12.5px]"
              options={[
                {
                  value: 'private',
                  label: (
                    <>
                      <Lock className="size-[13px] shrink-0" aria-hidden />
                      {assignmentLabels.private}
                    </>
                  ),
                },
                {
                  value: 'workspace',
                  label: (
                    <>
                      <Users className="size-[13px] shrink-0" aria-hidden />
                      {assignmentLabels.workspace}
                    </>
                  ),
                },
              ]}
            />
            <p className="text-[11.5px] leading-snug text-muted-foreground text-pretty">
              {crm.visibilityIsWorkspace
                ? assignmentLabels.hintWorkspace
                : assignmentLabels.hintPrivate}
            </p>
            {!crm.editable ? (
              <p className="text-[11.5px] text-muted-foreground">
                {crm.shareReadOnlyHint}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">{eventsSlot}</div>
        <div className="flex flex-col gap-1.5">{tagsSlot}</div>
      </CardContent>
    </Card>
  );
}

/**
 * Desktop/mobile lead detail side panels: Kontakt · Zuordnung · Herkunft.
 * CRM / HubSpot chrome intentionally omitted.
 */
export function LeadDetailSidebar({
  lead,
  locale,
  variant = 'desktop',
  labels,
  assignmentLabels,
  originLabels,
  statusLabels,
  sourceLabel,
  primaryEventName,
  consentDisplay,
  contactForm,
  isSavingContact,
  onContactFormChange,
  onContactFieldBlur,
  contactEmailError,
  onStatusChange,
  onCopy,
  crm,
  eventsSlot,
  tagsSlot,
}: LeadDetailSidebarProps) {
  const contactCard = (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
        <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
          {labels.contact}
        </CardTitle>
      </CardHeader>
      <CardContent className={LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS}>
        <LeadDetailProperties
          lead={lead}
          locale={locale}
          layout="contact"
          labels={labels}
          statusLabels={statusLabels}
          contactForm={contactForm}
          isSavingContact={isSavingContact}
          onContactFormChange={onContactFormChange}
          onContactFieldBlur={onContactFieldBlur}
          contactEmailError={contactEmailError}
          onStatusChange={onStatusChange}
          onCopy={onCopy}
        />
      </CardContent>
    </Card>
  );

  const originCard = (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
        <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
          {originLabels.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-[18px] pb-4 pt-0">
        <OriginRow
          icon={ScanLine}
          label={originLabels.source}
          value={sourceLabel}
        />
        <OriginRow
          icon={User}
          label={originLabels.capturedBy}
          value={crm.creatorValue}
        />
        <OriginRow
          icon={Calendar}
          label={originLabels.event}
          value={primaryEventName ?? '—'}
        />
        <OriginRow
          icon={ShieldCheck}
          label={originLabels.consent}
          value={consentDisplay}
        />
      </CardContent>
    </Card>
  );

  if (variant === 'mobile') {
    return (
      <div className="flex w-full min-w-0 flex-col gap-4">
        <AssignmentPanel
          crm={crm}
          assignmentLabels={assignmentLabels}
          eventsSlot={eventsSlot}
          tagsSlot={tagsSlot}
          showAssignee
        />
        {contactCard}
        {originCard}
      </div>
    );
  }

  return (
    <aside className="flex min-w-0 w-full flex-col gap-4 md:gap-5">
      {contactCard}
      <AssignmentPanel
        crm={crm}
        assignmentLabels={assignmentLabels}
        eventsSlot={eventsSlot}
        tagsSlot={tagsSlot}
        showAssignee={false}
      />
      {originCard}
    </aside>
  );
}

/** Compact field-group label used inside Zuordnung for Events/Tags. */
export function LeadDetailSidebarFieldLabel({
  children,
}: {
  children: ReactNode;
}) {
  return <span className="text-[12px] text-muted-foreground">{children}</span>;
}
